import { createElementVNode as _createElementVNode, createVNode as _createVNode, resolveDirective as _resolveDirective, openBlock as _openBlock, createElementBlock as _createElementBlock, withDirectives as _withDirectives, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, unref as _unref, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, mergeProps as _mergeProps } from "vue"

const _hoisted_1 = { class: "flex min-w-0 items-center gap-2" }
const _hoisted_2 = { class: "flex min-w-0 flex-col items-end" }
const _hoisted_3 = { class: "flex items-center gap-2" }
const _hoisted_4 = {
  "data-test": "menu-status-line",
  class: "hidden max-w-xs truncate text-xs text-gray-500 lg:block"
}
const _hoisted_5 = {
  key: 0,
  "data-test": "menu-error",
  role: "alert",
  class: "text-xs text-red-600"
}
const _hoisted_6 = {
  "data-test": "menu-sync-label",
  class: "sr-only sm:not-sr-only"
}
const _hoisted_7 = { class: "text-xs text-gray-500" }
const _hoisted_8 = {
  key: 0,
  class: "mt-2 text-xs text-gray-500"
}
const _hoisted_9 = { class: "mt-2 max-h-80 divide-y divide-gray-100 overflow-y-auto" }
const _hoisted_10 = { class: "flex items-baseline justify-between gap-2" }
const _hoisted_11 = {
  "data-test": "location-name",
  class: "truncate text-sm font-medium text-gray-900"
}
const _hoisted_12 = { class: "shrink-0 text-xs text-gray-500" }
const _hoisted_13 = {
  key: 0,
  class: "mt-1 space-y-0.5 text-xs text-red-600"
}

import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'

import FranchiseMenuEnableModal from '@/components/franchise/FranchiseMenuEnableModal.vue'
import NeueToggle from '@/components/neue/NeueToggle.vue'
import WizardConfirmModal from '@/components/neue/wizard/WizardConfirmModal.vue'
import { useConfirm } from '@/composables/useConfirm'
import { isTrackingMenuSync, stopMenuSync, trackMenuSync, useMenuSyncTracker } from '@/helpers/franchiseMenuTracker'
import { blueprintIdOf } from '@/helpers/franchiseMerchant'
import { useNow } from '@/helpers/reactiveNow'
import { timeAgo } from '@/helpers/timeAgo'
import api from '@/store/api'

const OFF_LINE = 'Off: edits to the franchise menu stay here'


export default {
  __name: 'FranchiseMenuControls',
  setup(__props) {

const CHIPS = {
  synced: { label: () => 'Synced', classes: 'bg-green-100 text-green-800', dot: 'bg-green-600' },
  syncing: { label: () => 'Syncing', classes: 'bg-amber-100 text-amber-800', dot: 'bg-amber-500' },
  errors: {
    label: count => `${count} ${count === 1 ? 'error' : 'errors'}`,
    classes: 'bg-red-100 text-red-800',
    dot: 'bg-red-600'
  },
  lost: { label: () => 'Status unknown', classes: 'bg-gray-100 text-gray-700', dot: 'bg-gray-500' }
}

const store = useStore()
const now = useNow()
const tracker = useMenuSyncTracker()
const { phase } = tracker
const { state: confirmState, ask, confirm: confirmYes, cancel: confirmNo } = useConfirm()

const modalOpen = ref(false)
const menuError = ref('')

const attributes = computed(() => store.state.franchise?.attributes ?? {})
const blueprintId = computed(() => blueprintIdOf(store.state))
const enabled = computed(() => !!attributes.value.menu_propagation_enabled)
const locationCount = computed(() => store.state.franchiseMerchants?.length ?? 0)

const lastSync = computed(() => {
  if (tracker.running.value) return 'syncing now'
  const finishedAt = attributes.value.menu_sync?.finished_at
  // now ticks once a minute; Date.now() keeps a fresh status from reading a minute old.
  return finishedAt ? `last sync ${timeAgo(finishedAt, Math.max(now.value, Date.now()))}` : 'not synced yet'
})

const statusLine = computed(() => {
  if (!enabled.value) return OFF_LINE
  const count = locationCount.value
  return `On: propagating to ${count} ${count === 1 ? 'location' : 'locations'}, ${lastSync.value}`
})

const chip = computed(() => {
  const look = CHIPS[phase.value]
  return look && { label: look.label(tracker.errorCount.value), classes: look.classes, dot: look.dot }
})

const keepStoreInStep = status => store.commit('SET_FRANCHISE_MENU_STATUS', status)

const track = initial => trackMenuSync({ call: api.call, initial, onStatus: keepStoreInStep })

const onEnabled = status => {
  modalOpen.value = false
  keepStoreInStep(status)
  track(status)
}

const turnOff = async () => {
  const yes = await ask({
    title: 'Turn off menu propagation?',
    message: 'Locations keep the menu as it is now and can edit it again',
    confirmLabel: 'Turn off',
    danger: true
  })
  if (!yes) return
  menuError.value = ''
  try {
    const response = await api.call('POST', 'franchise/menu/disable', {}, { receive: false })
    stopMenuSync()
    keepStoreInStep(response.data)
  } catch {
    menuError.value = 'Could not turn off menu propagation. Try again.'
  }
}

const onToggle = on => {
  if (on) modalOpen.value = true
  else turnOff()
}

// SecondaryNav renders its header twice (phone and desktop), so a second copy joins the first's polling.
const trackUnlessTracking = () => {
  if (!isTrackingMenuSync()) track()
}

onMounted(() => {
  if (enabled.value) trackUnlessTracking()
})
// A profile reload can turn it on; the status follows.
watch(enabled, (on, wasOn) => {
  if (on && !wasOn) trackUnlessTracking()
})
onBeforeUnmount(stopMenuSync)

return (_ctx, _cache) => {
  const _directive_tippy = _resolveDirective("tippy")

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _cache[1] || (_cache[1] = _createElementVNode("span", {
          "data-test": "menu-label",
          class: "hidden text-sm font-medium text-gray-700 lg:inline"
        }, "Menu propagation", -1)),
        _withDirectives((_openBlock(), _createElementBlock("span", null, [
          _createVNode(NeueToggle, {
            "model-value": enabled.value,
            disabled: !blueprintId.value,
            "aria-label": "Menu propagation",
            "onUpdate:modelValue": onToggle
          }, null, 8, ["model-value", "disabled"])
        ])), [
          [_directive_tippy, blueprintId.value ? null : { content: 'Create the franchise menu first' }]
        ])
      ]),
      _createElementVNode("p", _hoisted_4, _toDisplayString(statusLine.value), 1),
      (menuError.value)
        ? (_openBlock(), _createElementBlock("p", _hoisted_5, _toDisplayString(menuError.value), 1))
        : _createCommentVNode("", true)
    ]),
    (enabled.value && chip.value)
      ? (_openBlock(), _createBlock(_unref(Popover), {
          key: 0,
          class: "relative"
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(PopoverButton), {
              "data-test": "menu-sync-chip",
              class: _normalizeClass(['rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap', chip.value.classes])
            }, {
              default: _withCtx(() => [
                _createElementVNode("span", {
                  "data-test": "menu-sync-dot",
                  "aria-hidden": "true",
                  class: _normalizeClass(['block h-2 w-2 rounded-full sm:hidden', chip.value.dot])
                }, null, 2),
                _createElementVNode("span", _hoisted_6, _toDisplayString(chip.value.label), 1)
              ]),
              _: 1
            }, 8, ["class"]),
            _createVNode(_unref(PopoverPanel), {
              "data-test": "menu-sync-panel",
              class: "fixed inset-x-4 z-40 mt-2 rounded-lg bg-white p-3 text-left shadow-lg ring-1 ring-black/5 sm:absolute sm:inset-x-auto sm:right-0 sm:w-80"
            }, {
              default: _withCtx(() => [
                _createElementVNode("p", _hoisted_7, _toDisplayString(statusLine.value), 1),
                (_unref(phase) === 'lost')
                  ? (_openBlock(), _createElementBlock("p", _hoisted_8, " Lost touch with the sync. Reload the page to check on it. "))
                  : _createCommentVNode("", true),
                _createElementVNode("ul", _hoisted_9, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(tracker).status.value?.locations ?? [], (location) => {
                    return (_openBlock(), _createElementBlock("li", {
                      key: location.merchant_id,
                      "data-test": "menu-sync-location",
                      class: "py-2"
                    }, [
                      _createElementVNode("div", _hoisted_10, [
                        _createElementVNode("span", _hoisted_11, _toDisplayString(location.name), 1),
                        _createElementVNode("span", _hoisted_12, _toDisplayString(location.synced) + " of " + _toDisplayString(location.links) + " synced", 1)
                      ]),
                      (location.errors.length)
                        ? (_openBlock(), _createElementBlock("ul", _hoisted_13, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(location.errors, (failure, index) => {
                              return (_openBlock(), _createElementBlock("li", { key: index }, _toDisplayString(failure.message), 1))
                            }), 128))
                          ]))
                        : _createCommentVNode("", true)
                    ]))
                  }), 128))
                ])
              ]),
              _: 1
            })
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    _createVNode(FranchiseMenuEnableModal, {
      open: modalOpen.value,
      onClose: _cache[0] || (_cache[0] = $event => (modalOpen.value = false)),
      onEnabled: onEnabled
    }, null, 8, ["open"]),
    _createVNode(WizardConfirmModal, _mergeProps(_unref(confirmState), {
      onConfirm: _unref(confirmYes),
      onCancel: _unref(confirmNo)
    }), null, 16, ["onConfirm", "onCancel"])
  ]))
}
}

}